import * as turf from '@turf/turf';
import { MultiPolygon } from 'geojson';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { SnCurrentUser } from '@lib/auth';
import { GoogleMap, zoomToExtent } from '@lib/geo/google/map';
import { SnDialogService } from '@lib/ui/dialog';
import { BASE_COLORS } from '@lib/ui/theme';

@Component({
  selector: 'sn-alert-boundary-controls',
  templateUrl: './alert-boundary-controls.component.html',
  styleUrls: ['./alert-boundary-controls.component.scss'],
})
export class AlertBoundaryControlsComponent implements OnInit, OnDestroy {
  @Input() public map: GoogleMap;
  @Input() public isDrawing: string;

  @Output() public readonly draw = new EventEmitter<string>();
  @Output() public readonly save = new EventEmitter<void>();
  @Output() public readonly delete = new EventEmitter<void>();
  @Output() public readonly closeControls = new EventEmitter<void>();
  @Output() public readonly toggleOffPen = new EventEmitter<string>();

  public isLoading: boolean = false;
  public showError: boolean = false;
  public activeStrokeColor: string = BASE_COLORS['accent-400'];
  public activeFillColor: string = BASE_COLORS['accent-400'];
  public fillOpacity: number = 0.2;
  public strokeOpacity: number = 1;
  public strokeWeight: number = 6;
  public polygons: google.maps.Polygon[] = [];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _currentUser: SnCurrentUser,
    private readonly _dialogService: SnDialogService,
    private readonly _toastrService: ToastrService,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  get hasBoundary(): boolean {
    return this._currentUser.alertBoundary;
  }

  get activeBoundary(): boolean {
    return !!this._currentUser.alertBoundary;
  }

  get savedBoundary(): boolean {
    return !!this._currentUser.alertBoundary;
  }

  public ngOnInit(): void {
    this.getBoundary();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public drawBoundary(mode: string): void {
    this.showError = false;
    this._clearBoundary();
    this.draw.next(mode);
    this._cd.detectChanges();
  }

  public saveBoundary(close?: boolean): void {
    this.toggleOffPen.next(this.isDrawing);
    this.isLoading = true;
    const shape = this._currentUser.alertBoundary;
    const dataLayer = new google.maps.Data();
    switch (true) {
      case shape instanceof google.maps.Polygon:
        dataLayer.add(
          new google.maps.Data.Feature({
            geometry: new google.maps.Data.MultiPolygon([
              [
                shape
                  .getPath()
                  .getArray()
                  .map((latLng) => new google.maps.LatLng(latLng.lat(), latLng.lng())),
              ],
            ]),
            properties: {
              type: 'polygon',
            },
          }),
        );
        break;
    }
    dataLayer.toGeoJson((featureCollection: any) => {
      const feature = featureCollection.features[0];
      const payload = {
        alertsBoundary: turf.rewind(
          {
            type: feature?.geometry.type || feature.properties.type,
            coordinates: feature?.geometry.coordinates,
          },
          { mutate: true },
        ),
      };
      this._currentUser
        .updateUserBoundaries(payload)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            this.showError = false;
            this._createPolygons(response.alertsBoundary);
            if (close) {
              this.closeControlsBar(true);
            }
          },
          error: () => {
            this.isLoading = false;
            this._toastrService.error('Unable to update boundary. Please try again later.');
            this._clearBoundary();
            this.showError = true;
          },
        });
    });
  }

  public removeBoundary(): void {
    this.toggleOffPen.next(this.isDrawing);
    if (this._currentUser.alertBoundaryGeoJson) {
      this._dialogService
        .confirm({
          message: 'Are you sure you want to permanently delete the SAVED boundary?',
          confirmButton: {
            text: 'DELETE',
            color: 'warn',
          },
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this._deleteBoundary();
        });
    } else {
      this._clearBoundary();
    }
  }

  public centerBoundary(): void {
    this.toggleOffPen.next(this.isDrawing);
    const bounds = new google.maps.LatLngBounds();
    const shape = this._currentUser.alertBoundary;
    switch (true) {
      case shape instanceof google.maps.Polygon:
        const points = shape.getPath();
        this._processPoints(points, bounds.extend, bounds);
        break;
      case shape instanceof google.maps.Circle:
        const circlePoints = this._circlePath(shape.getCenter(), shape.getRadius(), 360).map(
          (coor) => new google.maps.LatLng(coor[0], coor[1]),
        );
        const geometry = new google.maps.Data.Polygon([circlePoints]);
        this._processPoints(geometry, bounds.extend, bounds);
        break;
    }
    this.map.mapInstance.fitBounds(bounds);
  }

  public closeControlsBar(bypassConfirm?: boolean): void {
    this.toggleOffPen.next(this.isDrawing);
    if (bypassConfirm) {
      this._clearBoundary();
      this.closeControls.next();
      return;
    }
    if (this._currentUser.alertBoundary) {
      this._dialogService
        .confirm({
          message: 'map.alertBoundaryControls.wouldYouLikeToSave',
          confirmButton: {
            text: 'common.save',
            color: 'success',
          },
          cancelButton: {
            text: 'common.close',
            color: 'warn',
          },
          actionOnClose: true,
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe((confirm) => {
          if (confirm) {
            this.saveBoundary(true);
          } else {
            this._clearBoundary();
            this.closeControls.next();
          }
        });
    } else {
      this._clearBoundary();
      this.closeControls.next();
    }
  }

  public reload(): void {
    this.toggleOffPen.next(this.isDrawing);
    this._clearBoundary();
    this.getBoundary();
  }

  public getBoundary(): void {
    this.isLoading = true;
    this._currentUser
      .getUserBoundaries()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.alertsBoundary) {
            const geojson: MultiPolygon = response.alertsBoundary;
            this._createPolygons(geojson, true);
          }
          this._cd.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this._toastrService.error(
            'Unable to load boundary. Please try reloading the boundary or try again later.',
          );
          this._cd.detectChanges();
        },
      });
  }

  private _deleteBoundary(): void {
    this.isLoading = true;
    const payload = {
      alertsBoundary: null,
    };
    this._currentUser
      .updateUserBoundaries(payload)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this._clearBoundary();
          this._currentUser.settings.mapSetting.alertsBoundary = null;
          this._currentUser.settings.alertsBoundary = null;
          this._cd.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this._toastrService.error('Unable to delete boundary. Please try again later.');
          this._cd.detectChanges();
        },
      });
  }

  private _processPoints(geometry: any, callback: any, thisArg: any): any {
    if (geometry instanceof google.maps.LatLng) {
      callback.call(thisArg, geometry);
    } else if (geometry instanceof google.maps.Data.Point) {
      callback.call(thisArg, geometry.get());
    } else {
      geometry.getArray().forEach((g) => {
        this._processPoints(g, callback, thisArg);
      });
    }
  }

  private _circlePath(center: google.maps.LatLng, radius: number, points: number): any[] {
    const paths = [];
    const factor = 360 / points;
    let distance = 0;
    for (let i = 0; i < points; i += 1, distance += factor) {
      const lat = google.maps.geometry.spherical.computeOffset(center, radius, distance).lat();
      const lng = google.maps.geometry.spherical.computeOffset(center, radius, distance).lng();
      paths.push([lat, lng]);
    }
    return paths;
  }

  private _clearBoundary(): void {
    if (this._currentUser.alertBoundary) {
      this._currentUser.alertBoundary.setMap(null);
      this._currentUser.alertBoundary = null;
    }
    this.showError = false;
    this._cd.detectChanges();
  }

  private _createPolygons(geojson: MultiPolygon, focusShape?: boolean): void {
    this.polygons = [];
    this._currentUser.alertBoundaryGeoJson = geojson;
    geojson.coordinates.forEach((coords) => {
      const paths = coords[0].map((position) => ({ lat: position[1], lng: position[0] }));
      const polygon = new google.maps.Polygon({
        paths,
        editable: true,
        draggable: true,
        strokeColor: this.activeStrokeColor,
        fillColor: this.activeFillColor,
        fillOpacity: this.fillOpacity,
        strokeOpacity: this.strokeOpacity,
        strokeWeight: this.strokeWeight,
      });
      this.polygons.push(polygon);
    });
    if (this.polygons.length) {
      if (this._currentUser.alertBoundary) {
        this._currentUser.alertBoundary.setMap(null);
        this._currentUser.alertBoundary = null;
      }
      this._currentUser.alertBoundary = this.polygons[0];
      this._currentUser.alertBoundary.setMap(this.map.mapInstance);
      if (focusShape) {
        zoomToExtent(this._currentUser.alertBoundary, this.map.mapInstance);
      }
    }
  }
}
